import { USER_ROLE } from '@skyrise-eng/types';
import { RATE_BUILDER_ROLE } from './roles';

export enum PRIVATE_PATH {
  MEDIA_PLAN = '/media-plan',
  RAISE_IO = '/media-plan/:id/raise-io',
  IO = '/io',
  RATE_CARD = '/rate-card',
  USER_SETTINGS = '/user-settings',
  TRADER_CONTROLS = '/trader-controls',
  TRADER_CONTROLS_CHANNELS = '/trader-controls/channels',
  TRADER_CONTROLS_DSP_FEES = '/trader-controls/dsp-fees',
  TRADER_CONTROLS_KPIS = '/trader-controls/kpis',
  TRADER_CONTROLS_TRADING_DEALS = '/trader-controls/trading-deals',
  EU6_CALCULATOR = '/eu6-calculator',
  CALCULATOR_RATE_CARD = '/calculator-rate-card',
  CUSTOM_PACKAGE = '/custom-package',
  BOOKING_SHEET = '/booking-sheet',
  TRADER_SHEET = '/trader-sheet',
  CREATIVE_LIBRARY = '/creative-library',
  DASHBOARD = '/dashboard',
  REPORTS = '/reports',
  FORECASTING = '/forecasting',
  FORECASTING_THREE_MONTH = '/forecasting/3-month',
  FORECASTING_MARGINS = '/forecasting/margins'
}

/** Financial Year + Current Quarter + Campaign Margins forecast views */
export const FORECASTING_GROUP_PATHS = [
  PRIVATE_PATH.FORECASTING,
  PRIVATE_PATH.FORECASTING_THREE_MONTH,
  PRIVATE_PATH.FORECASTING_MARGINS
] as const;

export enum PUBLIC_PATH {
  LOGIN = '/login'
}

/** Paths that appear under the "Custom Packages" submenu (custom package list + EU6 Calculator + Calculator Rate Card) */
export const CUSTOM_PACKAGE_GROUP_PATHS = [
  PRIVATE_PATH.CUSTOM_PACKAGE,
  PRIVATE_PATH.EU6_CALCULATOR,
  PRIVATE_PATH.CALCULATOR_RATE_CARD
] as const;

export const ALLOWED_PATHS = {
  [USER_ROLE.SUPER_ADMIN]: [
    PRIVATE_PATH.MEDIA_PLAN,
    PRIVATE_PATH.IO,
    PRIVATE_PATH.RATE_CARD,
    PRIVATE_PATH.CUSTOM_PACKAGE,
    PRIVATE_PATH.EU6_CALCULATOR,
    PRIVATE_PATH.CALCULATOR_RATE_CARD,
    PRIVATE_PATH.BOOKING_SHEET,
    PRIVATE_PATH.TRADER_SHEET,
    PRIVATE_PATH.CREATIVE_LIBRARY,
    PRIVATE_PATH.DASHBOARD,
    PRIVATE_PATH.REPORTS,
    PRIVATE_PATH.FORECASTING,
    PRIVATE_PATH.FORECASTING_THREE_MONTH,
    PRIVATE_PATH.FORECASTING_MARGINS,
    PRIVATE_PATH.TRADER_CONTROLS,
    PRIVATE_PATH.TRADER_CONTROLS_CHANNELS,
    PRIVATE_PATH.TRADER_CONTROLS_DSP_FEES,
    PRIVATE_PATH.TRADER_CONTROLS_KPIS,
    PRIVATE_PATH.TRADER_CONTROLS_TRADING_DEALS,
    PRIVATE_PATH.USER_SETTINGS
  ],
  [USER_ROLE.ADMIN]: [
    PRIVATE_PATH.MEDIA_PLAN,
    PRIVATE_PATH.IO,
    PRIVATE_PATH.RATE_CARD,
    PRIVATE_PATH.CUSTOM_PACKAGE,
    PRIVATE_PATH.EU6_CALCULATOR,
    PRIVATE_PATH.CALCULATOR_RATE_CARD,
    PRIVATE_PATH.BOOKING_SHEET,
    PRIVATE_PATH.TRADER_SHEET,
    PRIVATE_PATH.CREATIVE_LIBRARY,
    PRIVATE_PATH.DASHBOARD,
    PRIVATE_PATH.REPORTS,
    PRIVATE_PATH.FORECASTING,
    PRIVATE_PATH.FORECASTING_THREE_MONTH,
    PRIVATE_PATH.FORECASTING_MARGINS,
    PRIVATE_PATH.TRADER_CONTROLS,
    PRIVATE_PATH.TRADER_CONTROLS_CHANNELS,
    PRIVATE_PATH.TRADER_CONTROLS_DSP_FEES,
    PRIVATE_PATH.TRADER_CONTROLS_KPIS,
    PRIVATE_PATH.TRADER_CONTROLS_TRADING_DEALS
  ],
  [USER_ROLE.LEAD_TRADER]: [
    PRIVATE_PATH.MEDIA_PLAN,
    PRIVATE_PATH.IO,
    PRIVATE_PATH.RATE_CARD,
    PRIVATE_PATH.CUSTOM_PACKAGE,
    PRIVATE_PATH.EU6_CALCULATOR,
    PRIVATE_PATH.CALCULATOR_RATE_CARD,
    PRIVATE_PATH.BOOKING_SHEET,
    PRIVATE_PATH.TRADER_SHEET,
    PRIVATE_PATH.CREATIVE_LIBRARY,
    PRIVATE_PATH.DASHBOARD,
    PRIVATE_PATH.REPORTS,
    PRIVATE_PATH.FORECASTING,
    PRIVATE_PATH.FORECASTING_THREE_MONTH,
    PRIVATE_PATH.FORECASTING_MARGINS
  ],
  [USER_ROLE.TRADER]: [
    PRIVATE_PATH.MEDIA_PLAN,
    PRIVATE_PATH.IO,
    PRIVATE_PATH.RATE_CARD,
    PRIVATE_PATH.CUSTOM_PACKAGE,
    PRIVATE_PATH.EU6_CALCULATOR,
    PRIVATE_PATH.CALCULATOR_RATE_CARD,
    PRIVATE_PATH.BOOKING_SHEET,
    PRIVATE_PATH.TRADER_SHEET,
    PRIVATE_PATH.CREATIVE_LIBRARY,
    PRIVATE_PATH.DASHBOARD,
    PRIVATE_PATH.REPORTS,
    PRIVATE_PATH.FORECASTING,
    PRIVATE_PATH.FORECASTING_THREE_MONTH,
    PRIVATE_PATH.FORECASTING_MARGINS
  ],
  [USER_ROLE.FINANCE_MANAGER]: [
    PRIVATE_PATH.MEDIA_PLAN,
    PRIVATE_PATH.IO,
    PRIVATE_PATH.RATE_CARD,
    PRIVATE_PATH.CUSTOM_PACKAGE,
    PRIVATE_PATH.EU6_CALCULATOR,
    PRIVATE_PATH.CALCULATOR_RATE_CARD,
    PRIVATE_PATH.BOOKING_SHEET,
    PRIVATE_PATH.CREATIVE_LIBRARY,
    PRIVATE_PATH.DASHBOARD,
    PRIVATE_PATH.REPORTS,
    PRIVATE_PATH.FORECASTING,
    PRIVATE_PATH.FORECASTING_THREE_MONTH,
    PRIVATE_PATH.FORECASTING_MARGINS
  ],
  [USER_ROLE.ACCOUNT_MANAGER]: [
    PRIVATE_PATH.MEDIA_PLAN,
    PRIVATE_PATH.IO,
    PRIVATE_PATH.RATE_CARD,
    PRIVATE_PATH.CUSTOM_PACKAGE,
    PRIVATE_PATH.EU6_CALCULATOR,
    PRIVATE_PATH.CALCULATOR_RATE_CARD,
    PRIVATE_PATH.BOOKING_SHEET,
    PRIVATE_PATH.CREATIVE_LIBRARY,
    PRIVATE_PATH.DASHBOARD,
    PRIVATE_PATH.REPORTS,
    PRIVATE_PATH.FORECASTING,
    PRIVATE_PATH.FORECASTING_THREE_MONTH,
    PRIVATE_PATH.FORECASTING_MARGINS
  ],
  [USER_ROLE.CUSTOMER]: [PRIVATE_PATH.REPORTS],
  [RATE_BUILDER_ROLE]: [
    PRIVATE_PATH.CUSTOM_PACKAGE,
    PRIVATE_PATH.EU6_CALCULATOR,
    PRIVATE_PATH.CALCULATOR_RATE_CARD
  ]
};

/**
 * Where a role lands after signing in: the media plans for Skyrise staff, the
 * first page it may open otherwise. Sending a Rate Builder user to the media
 * plans bounced them to the login page, which sent them back again.
 */
export const homePathFor = (role?: string): string => {
  const paths: readonly string[] = role ? ALLOWED_PATHS[role as keyof typeof ALLOWED_PATHS] ?? [] : [];
  return paths.includes(PRIVATE_PATH.MEDIA_PLAN) ? PRIVATE_PATH.MEDIA_PLAN : paths[0] ?? PUBLIC_PATH.LOGIN;
};
